import { Library, Loader } from '@googlemaps/js-api-loader';
import { getStorefrontGoogleMapsApiKey } from '../../modules/storefront/exports/Cookies';

// The ONE place the Google Maps JS API is loaded from.
//
// It used to load app-wide from App.tsx on every page, which sent every visitor's IP to Google
// before they had done anything that needs a map. It now loads on demand, the first time a feature
// that actually needs it runs: the address search, geocoding an address the customer typed, or the
// map in the restaurant details. Those are all things the customer asked for.
//
// Anything that needs `window.google.maps` must go through `loadGoogleMaps()` (imperative code) or
// `useGoogleMaps()` (components). tests/google-third-party-boundary.test.ts enforces that no other
// loader exists.

const LIBRARIES: Library[] = ['places', 'geometry'];

const GOOGLE_FONTS_HOSTS = ['fonts.googleapis.com', 'fonts.gstatic.com'];

let loading: Promise<void> | null = null;
// The Loader from @googlemaps/js-api-loader is a singleton that throws when it is created again with
// different options. A retry must therefore reuse the options of the first attempt, even when the
// store's company changed in between (a location switch in a multi-location brand).
let loaderOptions: LoaderOptions | null = null;

type LoaderOptions = ConstructorParameters<typeof Loader>[0];
let fontsBlocked = false;

const isGoogleFontsNode = (node: Node): boolean => {
  if (node instanceof HTMLLinkElement) {
    return GOOGLE_FONTS_HOSTS.some((host) => node.href?.includes(host));
  }

  if (node instanceof HTMLStyleElement) {
    return GOOGLE_FONTS_HOSTS.some((host) => node.textContent?.includes(host));
  }

  return false;
};

// The Maps script injects a Roboto stylesheet from fonts.googleapis.com into <head>, which is the
// same Google Fonts transfer we removed from index.html. Drop it; GlobalStyles points `.gm-style`
// and `.pac-container` at DM Sans instead, so the map text does not fall back to Arial. This is the
// technique @react-google-maps' `preventGoogleFontsLoading` uses.
export function blockGoogleFonts(): void {
  if (fontsBlocked) {
    return;
  }

  fontsBlocked = true;

  const head = document.head;
  const insertBefore = head.insertBefore.bind(head);
  const appendChild = head.appendChild.bind(head);

  head.insertBefore = <T extends Node>(node: T, child: Node | null): T =>
    isGoogleFontsNode(node) ? node : insertBefore(node, child);
  head.appendChild = <T extends Node>(node: T): T => (isGoogleFontsNode(node) ? node : appendChild(node));
}

export function getGoogleMapsApiKey(): string {
  return getStorefrontGoogleMapsApiKey() ?? import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
}

/**
 * The language (and region) the Maps script answers in. Places and the Geocoder return address
 * components (street, city) in it, and the shop saves them as the delivery address, so it must be
 * the language of the shop, not the visitor's browser: without it a Russian browser got
 * "An der Klosterkoppel" back in Cyrillic and the receipt printed it that way.
 *
 * Order: the restaurant's own language when it is one of its region's locales (and not the trailing
 * 'en' fallback), otherwise the region's default locale, then the company language, then 'en'. The
 * region default alone is just `locales[0]`, which would load Maps in Dutch for a French-speaking
 * shop in Wallonia and turn the saved city "Liège" into "Luik".
 */
export async function getGoogleMapsLocale(): Promise<{ language: string; region?: string }> {
  // Imported on demand: the store pulls in every slice, which this loader's importers (and their
  // tests) should not load just to resolve a Maps script.
  const { store } = await import('../../store');
  const company = store.getState().initialData?.company;
  // Base language only ('de-DE' -> 'de'); the country goes in `region`.
  const baseLanguage = (locale?: string | null): string | undefined =>
    locale?.split(/[-_]/)[0]?.toLowerCase() || undefined;
  const companyLanguage = baseLanguage(company?.language);
  const regionLocales = (company?.region?.locales ?? []).map(baseLanguage);
  const shopLanguage =
    companyLanguage && companyLanguage !== 'en' && regionLocales.includes(companyLanguage)
      ? companyLanguage
      : undefined;

  return {
    language: shopLanguage || baseLanguage(company?.region?.default_locale) || companyLanguage || 'en',
    region: company?.region?.country || undefined,
  };
}

async function resolveLoaderOptions(): Promise<LoaderOptions> {
  if (!loaderOptions) {
    const locale = await getGoogleMapsLocale();

    loaderOptions ??= {
      apiKey: getGoogleMapsApiKey(),
      version: 'weekly',
      libraries: LIBRARIES,
      ...locale,
    };
  }

  return loaderOptions;
}

/** True once every library this app asks for (`LIBRARIES`) is on `window.google.maps`. */
export function isGoogleMapsReady(): boolean {
  return typeof window !== 'undefined' && !!window.google?.maps?.places && !!window.google.maps.geometry;
}

/**
 * Loads the Maps JS API (places + geometry) once and resolves when `window.google.maps` is usable.
 * Safe to call repeatedly; concurrent callers share one request. A failed load is not cached, so the
 * next caller retries.
 *
 * A load that "succeeds" without leaving places + geometry on `window.google` (a content blocker
 * stubbing the script, an extension clobbering the global) rejects too. Callers treat that like any
 * other load error and keep the plain address input, instead of mounting the autocomplete against a
 * missing API (the old `useLoadScript ... window.google is not present` invariant, FOODAMIGOS-SHOP-WEB-2EW).
 */
export function loadGoogleMaps(): Promise<void> {
  if (isGoogleMapsReady()) {
    return Promise.resolve();
  }

  if (!loading) {
    blockGoogleFonts();

    loading = resolveLoaderOptions()
      .then((options) => new Loader(options).load())
      .then(() => {
        if (!isGoogleMapsReady()) {
          throw new Error('Google Maps loaded, but window.google.maps is not usable');
        }
      })
      .catch((error) => {
        loading = null;

        throw error;
      });
  }

  return loading;
}

/** Test seam: forget the cached load so each test starts cold. */
export function resetGoogleMapsLoaderForTests(): void {
  loading = null;
  loaderOptions = null;
}
